import { useCallback, useEffect, useState } from "react";
import { Plus, RotateCcw, X } from "lucide-react";
import {
  DEFAULT_INK_SWATCHES,
  INK_SWATCHES_STORAGE_KEY,
  MAX_INK_SWATCHES,
  addInkSwatch,
  isLightInk,
  loadInkSwatches,
  normalizeHex,
  removeInkSwatch,
  saveInkSwatches,
} from "@/lib/inkSwatches";
import { cn } from "@/lib/utils";

type Props = {
  value?: string;
  onChange: (hex: string) => void;
  label?: string;
  /** Gdy true, ukrywa etykietę (np. gdy jest nad sekcją). */
  hideLabel?: boolean;
  storageKey?: string;
  defaults?: readonly string[];
  /** Zapamiętaj zaznaczenie tekstu przed otwarciem color pickera (skład slajdu). */
  onPickerMouseDown?: () => void;
  className?: string;
  swatchClassName?: string;
};

export default function EditableColorSwatches({
  value,
  onChange,
  label = "Kolor tekstu",
  hideLabel = false,
  storageKey = INK_SWATCHES_STORAGE_KEY,
  defaults = DEFAULT_INK_SWATCHES,
  onPickerMouseDown,
  className,
  swatchClassName,
}: Props) {
  const current = normalizeHex(value);
  const [swatches, setSwatches] = useState<string[]>(() => loadInkSwatches(storageKey, defaults));

  useEffect(() => {
    setSwatches(loadInkSwatches(storageKey, defaults));
  }, [storageKey, defaults]);

  const persist = useCallback(
    (next: string[]) => {
      setSwatches(next);
      saveInkSwatches(next, storageKey);
    },
    [storageKey],
  );

  const alreadySaved = swatches.some((c) => normalizeHex(c) === current);
  const atCap = swatches.length >= MAX_INK_SWATCHES;
  const canAdd = !alreadySaved && !atCap;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {!hideLabel ? (
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {label}
          <input
            type="color"
            className="h-8 w-10 cursor-pointer rounded border border-input bg-transparent p-0"
            value={current}
            onMouseDown={onPickerMouseDown}
            onChange={(e) => onChange(normalizeHex(e.target.value))}
          />
        </label>
      ) : (
        <input
          type="color"
          aria-label={label}
          className="h-8 w-10 cursor-pointer rounded border border-input bg-transparent p-0"
          value={current}
          onMouseDown={onPickerMouseDown}
          onChange={(e) => onChange(normalizeHex(e.target.value))}
        />
      )}
      {swatches.map((hex) => {
        const active = normalizeHex(hex) === current;
        const light = isLightInk(hex);
        return (
          <span key={hex} className="group relative inline-flex">
            <button
              type="button"
              title={`${hex} · kliknij, żeby wybrać · prawy / × usuwa`}
              aria-label={`Kolor ${hex}`}
              aria-pressed={active}
              className={cn(
                "h-6 w-6 rounded-full border",
                light ? "border-black/45" : "border-black/20",
                active && "ring-2 ring-brand/40 border-brand",
                swatchClassName,
              )}
              style={{ backgroundColor: hex }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange(normalizeHex(hex))}
              onContextMenu={(e) => {
                e.preventDefault();
                persist(removeInkSwatch(swatches, hex, defaults));
              }}
            />
            <button
              type="button"
              title="Usuń z listy"
              aria-label={`Usuń kolor ${hex}`}
              className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-slate-800 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                persist(removeInkSwatch(swatches, hex, defaults));
              }}
            >
              <X className="h-2.5 w-2.5" strokeWidth={3} />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        title={
          alreadySaved
            ? "Ten kolor już jest na liście"
            : atCap
              ? `Limit ${MAX_INK_SWATCHES} kolorów`
              : `Dodaj ${current} do listy`
        }
        aria-label="Dodaj kolor do listy"
        disabled={!canAdd}
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-black/30 text-muted-foreground",
          canAdd ? "hover:border-brand hover:text-brand" : "opacity-40 cursor-not-allowed",
        )}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!canAdd) return;
          persist(addInkSwatch(swatches, current));
        }}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
      </button>
      <button
        type="button"
        title="Przywróć domyślne kolory house"
        aria-label="Przywróć domyślne kolory"
        className="flex h-6 w-6 items-center justify-center rounded-full border border-black/20 text-muted-foreground hover:border-brand hover:text-brand"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => persist([...defaults])}
      >
        <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
      </button>
    </div>
  );
}
